import { Component, Inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Subject } from '../../../../shared/models/subject';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { SubjectType } from 'app/shared/models/subject-type';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { take } from 'rxjs/operators';
import { SubjectPrintService } from 'app/shared/services/subject-print.service';
import { ProcessTypeService } from 'app/shared/services/process-type.service';
import { ProcessType } from 'app/shared/models/process-type';
import { NoteService } from 'app/shared/services/note.service';
import { TaskService } from 'app/shared/services/task.service';
import { FileService } from 'app/shared/services/file.service';
import { SubjectService } from 'app/shared/services/subject.service';
import { DefaultDataService } from 'app/shared/services/default-data.service';
import { plural } from 'pluralize';
import { IEntity } from 'app/shared/interfaces/entity';
import { OnlineReportSettingService } from 'app/shared/services/online-report-setting.service';

@Component({
  selector: 'app-print-subject-dialog',
  templateUrl: './print-subject-dialog.component.html',
  styleUrls: ['./print-subject-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class PrintSubjectDialogComponent implements OnInit, OnDestroy {

  form: UntypedFormGroup;
  processTypes: ProcessType[] = [];
  subject: Subject;
  subjectType: SubjectType;
  canPrint = true;
  showLabels = false;
  showAssessments = false;
  showReferrals = false;
  showReviews = false;
  
  constructor(
    public dialogRef: MatDialogRef<PrintSubjectDialogComponent>,
    private formBuilder: UntypedFormBuilder,
    private subjectPrintService: SubjectPrintService,
    private subjectService: SubjectService,
    private processTypeService: ProcessTypeService,
    private noteService: NoteService,
    private taskService: TaskService,
    private fileService: FileService,
    private defaultDataService: DefaultDataService,
    private onlineReportSettingService :OnlineReportSettingService,
    @Inject(MAT_DIALOG_DATA) public data: any) {
      this.showLabels = true;
    }

  ngOnInit() {
    this.subject = this.data.subject;
    this.subjectType = this.data.subjectType;
    const isPrimary = this.subject.isPrimary || this.subject.subjectType.primary;
    this.showAssessments = (isPrimary && (this.defaultDataService.enableReferralsAndAssessments && this.defaultDataService.enableAssessments)) 
                            || (this.data.subject.subjectAssessments && (this.data.subject.subjectAssessments.length > 0));
    this.showReviews = (isPrimary && this.defaultDataService.enableReferralsAndAssessments) || (this.data.subject.subjectReviews && (this.data.subject.subjectReviews.length > 0));
    this.showReferrals = (isPrimary && this.defaultDataService.enableReferralsAndAssessments && 
                          (this.defaultDataService.enableReferrals || this.defaultDataService.enableMarkerSearch))
                          || (this.data.subject.subjectReferrals && (this.data.subject.subjectReferrals.length > 0));

    this.createForm();

    if (this.subjectType && this.subjectType.primary){
      // get available process types

      this.onlineReportSettingService.isDemographicRecordingEnabled().pipe(take(1)).subscribe((FTSUDemographcisInUse:boolean) => {        
        this.processTypeService.getAllData().pipe(take(1)).subscribe((resp: ProcessType[]) => {
          // sort and stash
          this.processTypes = resp.filter((i) => !i.hideRecord).sort((a, b) => a.name.localeCompare(b.name));

          // Remove Equality Monitoring processType if referral source from FTSU on subject        
          if (!this.getShowEqualityMonitoringWidget(this.subject)|| FTSUDemographcisInUse==false)
          {
            this.processTypes = this.processTypes.filter(i=>i.isFTSUDemographics==undefined || i.isFTSUDemographics==null || i.isFTSUDemographics==false);
          }

          this.processTypes.forEach((x) => (x as any).pluralName = plural(x.name));
          // build array of checkboxes for the process types
          const formArray = this.form.get('processTypes') as UntypedFormArray;
          this.processTypes.forEach((x) => formArray.push(new UntypedFormControl(true)));
        });
      });
    }
  }

  private getShowEqualityMonitoringWidget(entity: IEntity): boolean {
    const subject = entity as Subject;
    const referralSourceName = subject?.referral?.referralSource?.name?.toLowerCase();
    return (
      referralSourceName?.includes('freedom') &&
      referralSourceName?.includes('speak') &&
      referralSourceName?.includes('up')
    );
  }

  ngOnDestroy(){
  }

  createForm(){
    this.form = this.formBuilder.group({
      subject: [true],
      linkedSubjects: [true],
      linkedSubjectMarkers: [true],
      tasks: [true],
      listAttachments: [true],
      attachmentLabels: [''],
      notes: [true],
      includeDrafts: [true],
      onlyStarred: [false],
      onlyImportant: [false],
      includeSensitive: [true],
      markers: [true],
      assessments: [true],
      reviews: [true],
      referrals: [true],
      processTypes: new UntypedFormArray([]),
    });
    this.form.get('subject').disable();
  }

  print(){
    const val = this.form.getRawValue();
    const subjectId = this.subject.uid;
    this.subjectService.getFullSubject(subjectId).pipe(take(1)).subscribe((response: Subject) => {
      response.linkedSubjectMarkers = this.subject.linkedSubjectMarkers;
      this.subjectPrintService.print(response, {
        subject: val.subject,
        linkedSubjects: val.linkedSubjects,
        linkedSubjectMarkers: val.linkedSubjectMarkers,
        markers: val.markers,
        assessments: this.showAssessments && val.assessments,
        reviews: this.showReviews && val.reviews,
        referrals: this.showReferrals && val.referrals,
        processes: this.processTypes.filter((x, i) => !!val.processTypes[i]).map((x) => x.name),
        tasks: val.tasks && this.subjectType.primary,
        listAttachments: val.listAttachments,
        attachmentLabels: val.attachmentLabels,
        notes: (val.notes) ? {drafts: val.includeDrafts, starred: val.onlyStarred, important: val.onlyImportant, sensitive: val.includeSensitive} : null,
        noteOrderAscending: this.noteService.noteOrderAscending,
        tasksSortOrder: this.taskService.currentSortOrder,
      });
    });
  }
}
